import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Space, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { ABLE } from "@/meta/common";
import { BANK_CERT, INVOICE, INVOICE_HEADER } from "@/meta/customer/customer";
import { CHANNEL, COMMUNICATE_MODULE, INTENTION } from "@zhihaoscm/meta";
import type { CustomerInfo } from "@zhihaoscm/entity";
import Permission from "@/views/components/Permission";
import { AdminDetailTable } from "@/views/components/DetailTable";
import { useUserStore } from "@/stores/react/authStore";
import { useAreaLib } from "@/utils/hooks/useAreaLib";
import { confirmDialog } from "@/utils/confirmDialog";
import UserInfoForm from "./infoDetail/components/interactionRecord/userInfo/form";
import {
    CustomerCommunicationPage,
    type CustomerCommunicationQuery,
    type CustomerCommunicationRecord,
    loadCustomerCommunicationRecords
} from "./CustomerCommunicationPage";
import {
    CustomerLogisticsPage,
    type CustomerLogisticsQuery,
    type CustomerLogisticsRecord,
    loadCustomerLogistics
} from "./CustomerLogisticsPage";
import {
    CustomerProfilePage,
    type CustomerProfileBasic,
    type CustomerProfileOverview,
    type CustomerProfileStats,
    loadCustomerProfileBasic,
    loadCustomerProfileOverview,
    loadCustomerProfileStats
} from "./CustomerProfilePage";
import { CustomerSubAccountPage } from "./CustomerSubAccountPage";
import "./CustomerInfoDetailPage.scss";

interface CustomerDetailCustomer {
    id: number;
    code?: string;
    mobile?: string;
    email?: string;
    realName?: string;
    idNo?: string;
    createdTime?: string;
    updatedTime?: string;
    institutionName?: string;
    unifiedSocialCreditCode?: string;
    legalRepresentative?: string;
    memberLevel?: number | string;
    memberState?: number | string;
    memberActiveTime?: string;
    memberExpiryDate?: string;
    state?: number | string;
}

export interface CustomerInfoDetailRecord {
    customer: CustomerDetailCustomer;
    /** Vue CustomerVo 将微信昵称放在 customer 外层，需按原接口结构读取。 */
    nickName?: string;
    customerInfo?: CustomerInfo;
    institutionApply?: {
        institutionName?: string;
        unifiedSocialCreditCode?: string;
        legalRepresentative?: string;
    };
    isCancelInstitutionApply?: boolean;
    addresses?: Array<{
        id?: number;
        contacts?: string;
        institutionName?: string;
        cellphone?: string;
        areaCode?: string;
        landline?: string;
        zipCode?: string;
        detailAddress?: string;
        isDefault?: number;
    }>;
    banks?: Array<{
        bank?: { id?: number; name?: string; account?: string; bank?: string; type?: number };
        bankDict?: { bankName?: string };
    }>;
    invoiceHeaders?: Array<{
        id?: number;
        name?: string;
        headerType?: number;
        invoiceType?: number;
        taxpayerNo?: string;
        bank?: string;
        account?: string;
        mobile?: string | number;
        address?: string;
        isDefault?: number;
    }>;
}

export interface CustomerInfoDetailPageProps {
    userId?: number;
    loadDetail?: (id: number) => Promise<CustomerInfoDetailRecord>;
    cancelApply?: (id: number) => Promise<unknown>;
    loadLogistics?: (query: CustomerLogisticsQuery) => Promise<{ list?: CustomerLogisticsRecord[]; total?: number }>;
    loadCommunication?: (
        query: CustomerCommunicationQuery
    ) => Promise<{ list?: CustomerCommunicationRecord[]; total?: number }>;
    loadProfileBasic?: (params: { customerId: number }) => Promise<CustomerProfileBasic>;
    loadProfileOverview?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileOverview>;
    loadProfileStats?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileStats>;
    loadRechargeRecords?: (query: CustomerRechargeQuery) => Promise<{
        list?: CustomerRechargeRow[];
        total?: number;
    }>;
    loadRechargeAmount?: (customerId: number) => Promise<number>;
    canWriteCommunication?: boolean;
}

export interface CustomerRechargeQuery {
    page: number;
    size: number;
    customerId: number;
}

export interface CustomerRechargeRow {
    id: number | string;
    memberLevel?: number | string;
    memberDay?: number;
    openMode?: number | string;
    activationCode?: string;
    amount?: number;
    activationDate?: string;
    expireDate?: string;
    createdTime?: string;
}

const memberLevelLabels: Record<number, string> = { 1: "非会员", 2: "基础会员", 3: "高级会员", 4: "企业会员" };
const memberStateLabels: Record<number, string> = { 1: "激活", 2: "过期" };
const userStateLabels: Record<number, string> = { 0: "已禁用", 1: "已启用", 2: "禁用", 3: "已注销" };

type CustomerDetailTab = "base" | "member" | "subAccount" | "logistics" | "communication" | "profile";

/** 旧 Vue infoDetailHeader.vue 的内嵌页签顺序。 */
const customerDetailTabs: Array<{ key: CustomerDetailTab; label: string }> = [
    { key: "base", label: "基本信息" },
    { key: "member", label: "会员信息" },
    { key: "subAccount", label: "子账号信息" },
    { key: "logistics", label: "物流信息" },
    { key: "communication", label: "用户沟通记录" },
    { key: "profile", label: "用户画像" }
];

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 旧 Vue 会员摘要使用 dateFormat，仅展示日期部分。 */
const formatDateOnly = (value?: string) => (value ? value.replace("T", " ").slice(0, 10) : DEFAULT_VALUE);

/** 旧 Vue 详情页对过长邮箱做截断，避免账号信息撑开字段网格。 */
const formatEmail = (value?: string) => {
    if (!value) return DEFAULT_VALUE;
    return value.length > 32 ? `${value.slice(0, 32)}...` : value;
};

/** 复用 Vue `/admin/customer/find/:id` 用户详情接口。 */
export async function loadCustomerDetail(id: number): Promise<CustomerInfoDetailRecord> {
    const response = await request.service.get(`/admin/customer/find/${id}`);
    return response.data;
}

/** 保留 Vue 解除机构认证接口。 */
export async function cancelCustomerInstitutionApply(id: number) {
    const response = await request.service.put(`/admin/customer/cancelApply/${id}`);
    return response.data;
}

/** 复用 Vue 会员信息组件的充值记录分页接口，详情页只追加当前用户过滤条件。 */
export async function loadCustomerRechargeRecords(query: CustomerRechargeQuery) {
    const response = await request.service.get("/admin/member-open-record/paging", { params: query });
    return response.data as { list?: CustomerRechargeRow[]; total?: number };
}

/** 复用 Vue membershipInfo.vue 的总计实收金额接口。 */
export async function loadCustomerRechargeAmount(customerId: number) {
    const response = await request.service.get(`/admin/member-open-record/amount?customerId=${customerId}`);
    return Number(response.data || 0);
}

const membershipDays = (activeTime?: string, expiryTime?: string, state?: number | string) => {
    if (!activeTime || !expiryTime || Number(state) === 2) return "--";
    const days = Math.max(0, Math.round((new Date(expiryTime).getTime() - new Date(activeTime).getTime()) / 86400000));
    return `${days} 天`;
};

const rechargeMemberLevelLabels: Record<number, string> = { 2: "基础会员", 3: "高级会员", 4: "企业会员" };
const rechargeOpenModeLabels: Record<number, string> = {
    1: "微信支付",
    2: "支付宝",
    3: "激活码",
    4: "APPLE PAY"
};

/** 充值记录金额与 Vue 页面保持两位小数展示。 */
const formatRechargeAmount = (value?: number) => (value == null ? DEFAULT_VALUE : Number(value).toFixed(2));

/** 会员详情中的充值记录面板，负责分页数据的异步状态与核心字段展示。 */
function CustomerRechargeRecords({
    customerId,
    loadRecords,
    loadAmount
}: {
    customerId: number;
    loadRecords: (query: CustomerRechargeQuery) => Promise<{
        list?: CustomerRechargeRow[];
        total?: number;
    }>;
    loadAmount: (customerId: number) => Promise<number>;
}) {
    const [records, setRecords] = useState<CustomerRechargeRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [amount, setAmount] = useState<number>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        let disposed = false;
        setErrorMessage("");

        // 与 Vue membershipInfo.vue 一致，查询参数必须带当前详情用户 customerId。
        void loadRecords({ page, size: pageSize, customerId })
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
            })
            .catch(() => {
                if (disposed) return;
                setRecords([]);
                setTotal(0);
                setErrorMessage("会员充值记录加载失败");
            });

        return () => {
            disposed = true;
        };
    }, [customerId, loadRecords, page, pageSize]);

    useEffect(() => {
        let disposed = false;
        void loadAmount(customerId)
            .then(value => {
                if (!disposed) setAmount(value);
            })
            .catch(() => {
                if (!disposed) setAmount(0);
            });
        return () => {
            disposed = true;
        };
    }, [customerId, loadAmount]);

    // 用 zhihao-ui-react 列契约承接旧 Vue 会员充值记录的字段展示。
    const columns: TableColumnProps<CustomerRechargeRow>[] = [
        { key: "id", label: "编号", width: 148, prop: "id" },
        {
            key: "memberLevel",
            label: "会员等级",
            width: 160,
            prop: "memberLevel",
            render: ({ row }) => rechargeMemberLevelLabels[Number(row.memberLevel)] || DEFAULT_VALUE
        },
        {
            key: "openMode",
            label: "支付/兑换方式",
            width: 176,
            prop: "openMode",
            render: ({ row }) => rechargeOpenModeLabels[Number(row.openMode)] || DEFAULT_VALUE
        },
        { key: "activationCode", label: "激活码", width: 243, prop: "activationCode" },
        {
            key: "amount",
            label: "实收金额（元）",
            width: 228,
            prop: "amount",
            render: ({ row }) => formatRechargeAmount(row.amount)
        },
        {
            key: "activationDate",
            label: "激活日期",
            width: 128,
            prop: "activationDate",
            render: ({ row }) => formatDateOnly(row.activationDate)
        },
        {
            key: "expireDate",
            label: "到期日期",
            width: 117,
            prop: "expireDate",
            render: ({ row }) => formatDateOnly(row.expireDate)
        }
    ];

    return (
        <section aria-label="customer-recharge-records" className="customer-recharge-records">
            <AdminDetailTable<CustomerRechargeRow>
                data={records}
                columns={columns}
                pagination={false}
                // 真实数据超过一屏时沿用旧 Vue 的固定表头滚动；短列表不启用克隆表头，避免重复读屏节点。
                maxHeight={records.length > 5 ? "calc(100vh - 545px)" : undefined}
                rowKey="id"
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <h2 className="section-title">充值记录</h2>

                        <p>
                            总计实收金额<span className="customer-recharge-total-colon">：</span>
                            {amount == null ? DEFAULT_VALUE : amount.toFixed(2)} 元
                        </p>

                        {errorMessage ? (
                            <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                        ) : null}
                    </div>
                }
                append={
                    total > 0 ? (
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={page}
                                pageSize={pageSize}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                locale={{ jump_to: "转到" }}
                                showSizeChanger
                                total={total}
                                hideOnSinglePage
                                onChange={(nextPage, nextPageSize) => {
                                    // 切换每页条数时回到第一页，保持 Vue ProTable 行为。
                                    setPage(nextPageSize !== pageSize ? 1 : nextPage);
                                    setPageSize(nextPageSize);
                                }}
                            />
                        </div>
                    ) : null
                }
            />
        </section>
    );
}

const display = (value?: string | number | null) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

interface CustomerDetailCardItem {
    label: string;
    value: string;
}

/** 完整复刻旧 Vue InfoCard 的标题、默认标识和左右字段行。 */
function CustomerDetailInfoCard({
    title,
    isDefault,
    items
}: {
    title: string;
    isDefault?: boolean;
    items: CustomerDetailCardItem[];
}) {
    return (
        <Card
            className="admin-detail-info-card"
            extra={isDefault ? <span className="admin-detail-info-card__default">默认</span> : null}
            title={title}
        >
            {items.map(item => (
                <div className="admin-detail-info-card__row" key={item.label}>
                    <span>{item.label}</span>
                    <span>{item.value}</span>
                </div>
            ))}
        </Card>
    );
}

/** 旧版 baseInfo.vue 的关联卡片区，保留四档响应式列数和原始字段顺序。 */
function CustomerAssociatedInfo({ detail }: { detail: CustomerInfoDetailRecord }) {
    const addresses = detail.addresses || [];
    const banks = detail.banks || [];
    const invoiceHeaders = detail.invoiceHeaders || [];
    return (
        <section aria-label="customer-associated-info" className="customer-info-detail__associated">
            <div className="customer-info-detail__associated-block">
                <h2 className="section-title">地址簿</h2>
                {addresses.length ? (
                    <div className="admin-detail-card-grid">
                        {addresses.map(address => (
                            <CustomerDetailInfoCard
                                isDefault={Boolean(address.isDefault)}
                                items={[
                                    { label: "机构名称", value: display(address.institutionName) },
                                    { label: "手机号码", value: display(address.cellphone) },
                                    {
                                        label: "固定电话",
                                        value: address.landline
                                            ? `${address.areaCode ? `${address.areaCode}-` : ""}${address.landline}`
                                            : DEFAULT_VALUE
                                    },
                                    { label: "邮政编码", value: display(address.zipCode) },
                                    { label: "地址", value: display(address.detailAddress) }
                                ]}
                                key={address.id}
                                title={display(address.contacts)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="admin-detail-empty">暂无数据</div>
                )}
            </div>
            <div className="customer-info-detail__associated-block">
                <h2 className="section-title">银行账户</h2>
                {banks.length ? (
                    <div className="admin-detail-card-grid">
                        {banks.map(bank => (
                            <CustomerDetailInfoCard
                                items={[
                                    {
                                        label: "账户类型",
                                        value: BANK_CERT.getIntroByCode(bank.bank?.type) || DEFAULT_VALUE
                                    },
                                    { label: "开户名称", value: display(bank.bank?.name) },
                                    { label: "银行账户", value: display(bank.bank?.account) },
                                    { label: "银行", value: display(bank.bankDict?.bankName || bank.bank?.bank) }
                                ]}
                                key={bank.bank?.id}
                                title={display(bank.bank?.bank || bank.bankDict?.bankName)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="admin-detail-empty">暂无数据</div>
                )}
            </div>
            <div className="customer-info-detail__associated-block">
                <h2 className="section-title">发票抬头</h2>
                {invoiceHeaders.length ? (
                    <div className="admin-detail-card-grid">
                        {invoiceHeaders.map(invoice => (
                            <CustomerDetailInfoCard
                                isDefault={Boolean(invoice.isDefault)}
                                items={[
                                    {
                                        label: "抬头类型",
                                        value: INVOICE_HEADER.getIntroByCode(invoice.headerType) || DEFAULT_VALUE
                                    },
                                    {
                                        label: "发票类型",
                                        value: INVOICE.getIntroByCode(invoice.invoiceType) || DEFAULT_VALUE
                                    },
                                    { label: "纳税人识别号", value: display(invoice.taxpayerNo) },
                                    { label: "开户行", value: display(invoice.bank) },
                                    { label: "账号", value: display(invoice.account) },
                                    { label: "电话", value: display(invoice.mobile) },
                                    { label: "地址", value: display(invoice.address) }
                                ]}
                                key={invoice.id}
                                title={display(invoice.name)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="admin-detail-empty">暂无数据</div>
                )}
            </div>
        </section>
    );
}

/** 迁移旧版沟通用户信息区，保留字段网格及有写权限时的修改入口。 */
function CustomerCommunicationUserInfo({
    info,
    userId,
    onRefresh
}: {
    info?: CustomerInfo;
    userId: number;
    onRefresh: () => Promise<void>;
}) {
    const banButton = useUserStore(user => user.banButton);
    // 地区库异步加载完成后触发详情重绘，避免保存后编码已存在但名称仍显示 `--`。
    const areaLibrary = useAreaLib();
    const [editing, setEditing] = useState(false);
    const intention = info?.intention ? INTENTION.getIntroByCode(info.intention)?.slice(0, 1) : DEFAULT_VALUE;
    const yesNo = (value?: number) => (value == null ? DEFAULT_VALUE : value ? "是" : "否");
    const concernModule = info?.concernModule?.length
        ? info.concernModule.map(item => COMMUNICATE_MODULE.getIntroByCode(item)).join("、")
        : DEFAULT_VALUE;
    const userLabel = info?.userLabel?.length ? info.userLabel.join("、") : DEFAULT_VALUE;
    const infoWithAreaNames = info as (CustomerInfo & { provinceName?: string; cityName?: string }) | undefined;
    // 旧 Vue 通过 areaLib 展示省市名称，不能把行政区编码直接显示给用户。
    const region =
        areaLibrary.getFullAreaName(info?.provinceCode, info?.cityCode) ||
        [infoWithAreaNames?.provinceName, infoWithAreaNames?.cityName].filter(Boolean).join("/") ||
        (info?.provinceCode || info?.cityCode
            ? [info.provinceCode, info.cityCode].filter(Boolean).join("/")
            : "");
    return (
        <section
            aria-label="customer-communication-user-info"
            className={`admin-detail-section customer-communication-user-info${editing ? " is-editing" : ""}`}
        >
            <h2 className="section-title">用户信息</h2>
            {editing ? (
                <UserInfoForm
                    info={info as Record<string, any> | undefined}
                    userId={userId}
                    onCancel={() => setEditing(false)}
                    onSaved={() => {
                        setEditing(false);
                        void onRefresh();
                    }}
                />
            ) : (
                <>
                    <ZhGrid cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }} gap={[20, 20]}>
                        <ZhBaseItem label="姓名" value={display(info?.name)} />
                        <ZhBaseItem label="区域" value={region || "\u00a0"} />
                        <ZhBaseItem label="了解渠道" value={CHANNEL.getIntroByCode(info?.channel) || DEFAULT_VALUE} />
                        <ZhBaseItem label="意向度" value={intention} />
                        <ZhBaseItem label="是否为砂石行业" value={yesNo(info?.sandFlag)} />
                        <ZhBaseItem label="行业" value={display(info?.industry)} />
                        <ZhBaseItem label="是否需要入驻平台" value={yesNo(info?.settlePlatformFlag)} />
                        <ZhBaseItem label="关注模块" value={concernModule} />
                        <ZhBaseItem label="用户标签" value={userLabel} span={4} />
                    </ZhGrid>
                    <Permission anyOf={["ROLE_SS_CUSTOMER_W"]}>
                        <div className="customer-communication-user-info__actions">
                            <Button disabled={banButton} type="primary" onClick={() => setEditing(true)}>
                                修改
                            </Button>
                        </div>
                    </Permission>
                </>
            )}
        </section>
    );
}

export function CustomerInfoDetailPage({
    userId,
    loadDetail = loadCustomerDetail,
    cancelApply = cancelCustomerInstitutionApply,
    loadLogistics = loadCustomerLogistics,
    loadCommunication = loadCustomerCommunicationRecords,
    loadProfileBasic = loadCustomerProfileBasic,
    loadProfileOverview = loadCustomerProfileOverview,
    loadProfileStats = loadCustomerProfileStats,
    loadRechargeRecords = loadCustomerRechargeRecords,
    loadRechargeAmount = loadCustomerRechargeAmount,
    canWriteCommunication = true
}: CustomerInfoDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const banButton = useUserStore(user => user.banButton);
    // Vue 详情页从 route.query.userId 取值；保留 prop 覆盖能力供嵌入式调用和测试使用。
    const routeUserId = new URLSearchParams(location.search).get("userId");
    const resolvedUserId =
        userId ?? (routeUserId && Number.isFinite(Number(routeUserId)) ? Number(routeUserId) : undefined);
    const [detail, setDetail] = useState<CustomerInfoDetailRecord>();
    const [activeTab, setActiveTab] = useState<CustomerDetailTab>("base");
    const [errorMessage, setErrorMessage] = useState("");

    const fetchDetail = () => {
        if (resolvedUserId == null) return Promise.resolve();
        return loadDetail(resolvedUserId)
            .then(result => {
                setDetail(result);
                setErrorMessage("");
            })
            .catch(() => setErrorMessage("用户详情加载失败"));
    };

    useEffect(() => {
        let disposed = false;
        if (resolvedUserId == null) return undefined;
        void loadDetail(resolvedUserId)
            .then(result => {
                if (!disposed) {
                    setDetail(result);
                    setErrorMessage("");
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, resolvedUserId]);

    const customer = detail?.customer;
    const institutionName = customer?.institutionName || detail?.institutionApply?.institutionName;
    const creditCode = customer?.unifiedSocialCreditCode || detail?.institutionApply?.unifiedSocialCreditCode;
    const legalRepresentative = customer?.legalRepresentative || detail?.institutionApply?.legalRepresentative;

    const handleCancelApply = async () => {
        const customerId = customer?.id ?? resolvedUserId;
        if (customerId == null) return;
        try {
            await cancelApply(customerId);
            message.success("机构认证已解除");
            await fetchDetail();
        } catch {
            message.error("解除机构认证失败");
        }
    };

    /** 最新禅道验收要求解除机构认证前显示二次确认，与截图中的提示弹窗保持一致。 */
    const confirmCancelApply = () => {
        void confirmDialog("是否解除该用户的组织机构认证", "提示", {
            onOk: handleCancelApply
        });
    };

    return (
        <section
            aria-label="customer-info-detail-page"
            className="customer-info-detail admin-detail-page detail-page-transparent"
            data-active-tab={activeTab}
        >
            <div className="customer-info-detail__surface">
                <div className="customer-info-detail__header admin-detail-header-card">
                    <ZhDetailHeader
                        rightInfos={[{ label: "更新时间", value: formatDate(customer?.updatedTime) }]}
                        stateDesc={
                            ABLE.getIntroByCode(customer?.state) ||
                            userStateLabels[Number(customer?.state)] ||
                            DEFAULT_VALUE
                        }
                        title="用户详情"
                    />
                    {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                    <div aria-label="用户详情标签" className="admin-detail-tabs" role="tablist">
                        {customerDetailTabs.map(tab => (
                            <button
                                aria-pressed={activeTab === tab.key}
                                className={activeTab === tab.key ? "is-active" : undefined}
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="customer-info-detail__content">
                    {activeTab === "profile" && customer?.id != null ? (
                        <CustomerProfilePage
                            customerId={customer.id}
                            onBack={() => navigate(-1)}
                            loadBasic={loadProfileBasic}
                            loadOverview={loadProfileOverview}
                            loadStats={loadProfileStats}
                        />
                    ) : activeTab === "communication" && customer?.id != null ? (
                        <>
                            <CustomerCommunicationUserInfo
                                info={detail?.customerInfo}
                                userId={customer.id}
                                onRefresh={fetchDetail}
                            />
                            <CustomerCommunicationPage
                                customerId={customer.id}
                                canRead
                                canWrite={canWriteCommunication}
                                loadRecords={loadCommunication}
                            />
                        </>
                    ) : activeTab === "logistics" && customer?.id != null ? (
                        <CustomerLogisticsPage customerId={customer.id} loadLogistics={loadLogistics} />
                    ) : activeTab === "subAccount" && customer?.id != null ? (
                        <CustomerSubAccountPage mainAccountId={customer.id} />
                    ) : activeTab === "base" ? (
                        <section className="admin-detail-section" aria-label="用户基本信息">
                            {/* 每个标题和字段网格对应 Vue detailJson 的一个 block；block 之间只保留页面灰色间距，不额外绘制卡片。 */}
                            <div className="customer-info-detail__block">
                                <h2 className="section-title">账号信息</h2>
                                <ZhGrid cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }} gap={[12, 12]}>
                                    <ZhBaseItem colon={false} label="用户ID" value={display(customer?.code)} />
                                    <ZhBaseItem colon={false} label="注册手机号" value={display(customer?.mobile)} />
                                    <ZhBaseItem colon={false} label="注册时间" value={formatDate(customer?.createdTime)} />
                                    <ZhBaseItem
                                        colon={false}
                                        label="绑定微信"
                                        value={display(detail?.nickName === customer?.mobile ? undefined : detail?.nickName)}
                                    />
                                    <ZhBaseItem colon={false} label="绑定邮箱" value={formatEmail(customer?.email)} />
                                </ZhGrid>
                            </div>
                            <div className="customer-info-detail__block">
                                <h2 className="section-title">个人认证信息</h2>
                                <ZhGrid cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }} gap={[12, 12]}>
                                    <ZhBaseItem colon={false} label="个人实名" value={display(customer?.realName)} />
                                    <ZhBaseItem colon={false} label="身份证号" value={customer?.idNo || "未认证"} />
                                </ZhGrid>
                            </div>
                            <div className="customer-info-detail__block">
                                <h2 className="section-title">组织机构信息</h2>
                                <ZhGrid cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }} gap={[12, 12]}>
                                    <ZhBaseItem colon={false} label="组织机构名称" value={display(institutionName)} />
                                    <ZhBaseItem colon={false} label="统一社会信用代码" value={creditCode || "未认证"} />
                                    <ZhBaseItem colon={false} label="法人代表" value={legalRepresentative || "未认证"} />
                                </ZhGrid>
                            </div>
                            {detail ? <CustomerAssociatedInfo detail={detail} /> : null}
                        </section>
                    ) : (
                        <section className="admin-detail-section customer-member-section" aria-label="用户会员信息">
                            <div className="customer-info-detail__block">
                                <h2 className="section-title">会员信息</h2>
                                <ZhGrid
                                    className="customer-member-info-grid"
                                    cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }}
                                    gap={[12, 12]}
                                >
                                    <ZhBaseItem
                                        colon={false}
                                        label="等级"
                                        labelWidth="auto"
                                        value={memberLevelLabels[Number(customer?.memberLevel)] || DEFAULT_VALUE}
                                    />
                                    <ZhBaseItem
                                        colon={false}
                                        label="状态"
                                        labelWidth="auto"
                                        span={3}
                                        value={memberStateLabels[Number(customer?.memberState)] || DEFAULT_VALUE}
                                    />
                                    <ZhBaseItem
                                        colon={false}
                                        label="时长"
                                        labelWidth="auto"
                                        value={membershipDays(
                                            customer?.memberActiveTime,
                                            customer?.memberExpiryDate,
                                            customer?.memberState
                                        )}
                                    />
                                    <ZhBaseItem
                                        colon={false}
                                        label="激活日期"
                                        labelWidth="auto"
                                        value={formatDateOnly(customer?.memberActiveTime)}
                                    />
                                    <ZhBaseItem
                                        colon={false}
                                        label="到期时间"
                                        labelWidth="auto"
                                        value={formatDateOnly(customer?.memberExpiryDate)}
                                    />
                                </ZhGrid>
                            </div>
                            {customer?.id != null ? (
                                <CustomerRechargeRecords
                                    customerId={customer.id}
                                    loadRecords={loadRechargeRecords}
                                    loadAmount={loadRechargeAmount}
                                />
                            ) : (
                                <p>充值记录：{DEFAULT_VALUE}</p>
                            )}
                        </section>
                    )}
                </div>
            </div>
            <Space className="operation-fixed-btn" size={0}>
                {/* 对齐旧版 infoDetail 的固定返回操作，回到进入详情前的历史页面。 */}
                <Button aria-label="返回" onClick={() => navigate(-1)}>
                    返回
                </Button>
                {detail?.isCancelInstitutionApply ? (
                    <Permission anyOf={["ROLE_SS_CUSTOMER_W"]}>
                        <Button disabled={banButton} onClick={confirmCancelApply}>
                            解除机构认证
                        </Button>
                    </Permission>
                ) : null}
            </Space>
        </section>
    );
}
